import React, {useState, useEffect, useRef, useMemo} from 'react';
import Modal from '../Modal';
import Icon from '../Icons/Icon';
import { useCustomers } from '../../Data/Customers';
import { liveSearch } from '../Search/liveSearch';
import { toQueryString, useDebounce } from '../../Services/helpers';
import { Link } from 'react-router-dom';
import { useRounds } from '../../Data/Rounds';
import { useQuotes } from '../../Data/Quotes';
import Loading from '../Loading';
import { CustomerResult, RoundResult, QuoteResult, PageResult } from './SearchResults';
import Recents from './Recents';
import pages from './pages';

const GlobalSearch = ({isOpen, setIsOpen}) => {
    return <Modal isOpen={isOpen} setIsOpen={setIsOpen} title="Search" dismissible>
        {isOpen && <Inner close={() => setIsOpen(false)} />}
    </Modal>;
};

const Inner = ({close}) => {
    const [search, setSearch] = useState('');
    const searchDebounced = useDebounce(search, 500);
    const inputRef = useRef(null);
    const [searching, setSearching] = useState({customers: false, rounds: false, quotes: false, pages: false});
    const [results, setResults] = useState({customers: false, rounds: false, quotes: false, pages: false});

    const isSearching = Object.values(searching).some(v => v);
    const hasNoResults = Object.values(results).every(v => v === 0);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const link = (query) => query.replace('{q}', toQueryString(search));

    return <div className="flex flex-col gap-4 max-h-full">

        <div className="flex flex-row items-center gap-2 w-full rounded-full border border-gray-200 dark:border-slate-700 surface-2 p-2 text-lg focus-within:ring-2 focus-within:ring-brand">
            <Icon icon="MagnifyingGlass" size="2x" className="fill-slate-800 dark:fill-slate-200" />
            <input type="text"
                ref={inputRef}
                placeholder="Search anything"
                className="p-0 flex-1 border-none outline-none bg-transparent ring-0 text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            {isSearching && <Loading size="2x" className="mr-2" />}
        </div>

        <div className="max-h-full min-h-32 overflow-y-auto no-scrollbar">
            <Customers {...{
                search: searchDebounced,
                link,
                close,
                searching: (v) => searching.customers !== v && setSearching({...searching, customers: v}),
                results: (v) => results.customers !== v && setResults(x => ({...x, customers: v})),
            }} />
            <Rounds {...{
                search: searchDebounced,
                link,
                close,
                searching: (v) => searching.rounds !== v && setSearching({...searching, rounds: v}),
                results: (v) => results.rounds !== v && setResults(x => ({...x, rounds: v})),
            }} />
            <Quotes {...{
                search: searchDebounced,
                link,
                close,
                searching: (v) => searching.quotes !== v && setSearching({...searching, quotes: v}),
                results: (v) => results.quotes !== v && setResults(x => ({...x, quotes: v})),
            }} />
            <Pages {...{
                search: searchDebounced,
                close,
                searching: (v) => searching.pages !== v && setSearching({...searching, pages: v}),
                results: (v) => results.pages !== v && setResults(x => ({...x, pages: v})),
            }} />

            {(hasNoResults && !isSearching && searchDebounced) && <p className="my-auto text-center text-lg text-muted">
                No results found
            </p>}

            {!searchDebounced && <Recents close={close} />}

            {!hasNoResults && <p className="my-6 text-center text-sm text-muted mx-auto max-w-[75%] text-balance">
                Search customers, rounds, quotes and more
            </p>}
        </div>
    </div>;
};

const SearchComponent = ({ data, title, isLoading, search, row, showAll, searching, results, close }) => {
    const matches = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }
        return data.filter(liveSearch(search)).slice(0, 5);
    }, [data, search]);

    useEffect(() => {
        searching(isLoading);
        results(matches.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, search, isLoading]);

    if (!search) {
        return null;
    }

    if (matches.length === 0) {
        return null;
    }

    const hasMore = data && data.length > matches.length;

    return <Results title={title} showAll={(hasMore && showAll) ? showAll : null} close={close}>
        {matches.map(row)}
    </Results>
};

const Customers = ({ search, link, close, searching, results }) => {
    const {customers, isLoading} = useCustomers({}, true);

    return <SearchComponent
        {...{
            data: customers,
            isLoading,
            search,
            link,
            close,
            searching,
            results,
            title: 'Customers',
            showAll: link('/customers?search={q}'),
            row: (customer) => <CustomerResult key={customer.id} customer={customer} close={close} />
        }}
    />
};

const Rounds = ({search, link, close, searching, results}) => {
    const {rounds, isLoading} = useRounds({}, true);

    return <SearchComponent
        {...{
            data: rounds,
            isLoading,
            search,
            link,
            close,
            searching,
            results,
            title: 'Rounds',
            showAll: link('/rounds?search={q}'),
            row: (round) => <RoundResult key={round.id} round={round} close={close} />
        }}
    />
}

const Quotes = ({search, link, close, searching, results}) => {
    const {quotes, isLoading} = useQuotes({}, true);

    return <SearchComponent
        {...{
            data: quotes,
            isLoading,
            search,
            link,
            close,
            searching,
            results,
            title: 'Quotes',
            showAll: link('/quotes?search={q}'),
            row: (quote) => <QuoteResult key={quote.id} quote={quote} close={close} />
        }}
    />
}

const Pages = ({search, close, searching, results}) => {
    return <SearchComponent
        {...{
            data: pages,
            isLoading: false,
            search,
            close,
            searching,
            results,
            title: 'Pages',
            row: (page) => <PageResult key={page.to} page={page} close={close} />
        }}
    />
}

const Results = ({title, children, showAll, close}) => {
    return <div className="flex flex-col gap-3 py-2">
        <div className="text-sm font-bold text-slate-400 dark:text-slate-500 flex flex-row items-center justify-between">
            {title}
            {showAll && <span>
                <Link to={showAll}
                    onClick={close}
                    className="text-sm text-slate-400 dark:text-slate-500 font-normal hover:text-slate-600 dark:hover:text-slate-300"
                >
                    Show all
                </Link>
            </span>}
        </div>
        {children}
    </div>;
};

export default GlobalSearch;
